// src/features/owner/sections/appeals/AppealsSection.tsx — sanction-appeal review queue.
import { useOwnerAppeals } from '@/hooks/useOwnerAppeals';
import { useSession } from '@/hooks/useSession';
import { Button } from '@/ui';
import { ownerService } from '@/core/services/ownerService';
import { OwnerPanel, DataTable } from '../../components';
import type { Column } from '../../components';
import type { AppealRow } from '@/core/models';

export function AppealsSection() {
  const { data, loading, error, refetch } = useOwnerAppeals();
  const { isSuperuser } = useSession();
  const rows: AppealRow[] = data?.results ?? [];

  const columns: Column<AppealRow>[] = [
    {
      key: 'user',
      header: 'User',
      sortBy: (r) => r.handle,
      render: (r) => `${r.username} ${r.handle}`,
    },
    {
      key: 'kind',
      header: 'Kind',
      render: (r) => r.kind,
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (r) => r.reason,
    },
    {
      key: 'submitted',
      header: 'Submitted',
      sortBy: (r) => r.createdAt,
      render: (r) => new Date(r.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          {(r.kind !== 'ban' || isSuperuser) && (
            <Button
              variant="ghost"
              onClick={async () => {
                await ownerService.reviewAppeal(r.id, 'approve');
                refetch();
              }}
            >
              Approve
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={async () => {
              await ownerService.reviewAppeal(r.id, 'reject');
              refetch();
            }}
          >
            Reject
          </Button>
        </span>
      ),
    },
  ];

  return (
    <OwnerPanel title="Appeals">
      {loading && <p>Loading…</p>}
      {error && <p>{error}</p>}
      {!loading && !error && (
        <DataTable<AppealRow>
          columns={columns}
          rows={rows}
          rowKey={(r) => String(r.id)}
          emptyIcon="inbox"
          emptyMessage="No pending appeals"
        />
      )}
    </OwnerPanel>
  );
}
